import type { TFunction } from 'i18next';
import { array, boolean, number, object, string, type ZodType } from 'zod';

import type { PollFeature } from '@/features/feature-management/types/pollFeature';
import { DATE_TIME_FORMAT } from '@/features/post-editor/constants/dateTime';
import type { OptionText, PollCreate } from '@/features/post-editor/types/poll';
import { getFormattedDate } from '@/shared/utils/date';
import { unique } from '@/shared/utils/zod';
import { dayjs, TOMORROW } from '@/time';

type Props = {
  t: TFunction<'post-editor', 'components.poll.form'>;
  pollFeatureValues: PollFeature['featureValues'];
};

export const createOptionTextSchema = ({ t, pollFeatureValues }: Props) =>
  object({
    optionText: string()
      .trim()
      .min(1, t('option.error.empty'))
      .max(
        pollFeatureValues.maxNumberCharsPollOptionText,
        t('option.error.max', {
          maxChar: pollFeatureValues.maxNumberCharsPollOptionText
        })
      )
  }) satisfies ZodType<OptionText>;

export const createPollCreateSchema = ({ t, pollFeatureValues }: Props) => {
  const OptionTextSchema = createOptionTextSchema({ t, pollFeatureValues });

  return object({
    text: string()
      .min(1, t('question.error.empty'))
      .max(
        pollFeatureValues.maxNumberCharsPollText,
        t('question.error.max', {
          maxChar: pollFeatureValues.maxNumberCharsPollText
        })
      ),
    multipleChoice: boolean(),
    expirationTime: number()
      .min(TOMORROW.getTime(), t('date.error.past'))
      .max(
        dayjs()
          .add(pollFeatureValues.maxPollDurationMilliseconds, 'millisecond')
          .toDate()
          .getTime(),
        t('date.error.future', {
          dateTime: getFormattedDate(
            dayjs()
              .add(pollFeatureValues.maxPollDurationMilliseconds, 'millisecond')
              .toDate(),
            DATE_TIME_FORMAT
          )
        })
      ),
    optionTexts: array(OptionTextSchema)
      .min(2)
      .max(5)
      .superRefine(
        unique([{ key: 'optionText', message: t('option.error.unique') }])
      )
  }) satisfies ZodType<PollCreate>;
};
